import { useEffect, useMemo, useState } from 'react';
import { Badge, Col, Form, Row } from 'antd';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon, EditIcon, ExcelIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import { EyeOutlined, FileOutlined } from '@ant-design/icons';
import AnalyticsApi from '../../../../libs/api/analytics';
import { useNavigate } from '../../../../hooks/useNavigate';
import { useRecoilValue } from 'recoil';
import { getShoppingCenters } from '../../../../hooks/useUser';
import { getShops } from '../../../../hooks/useShops';
import { useApi } from '../../../../hooks/useApi';
import EmergencyContactsAdapter from '../../../facility/subPages/emergencyContacts/emergencyContacts.adapter';
import UIHelper from '../../../../helpers/UIHelper';

const api = new AnalyticsApi('calendar_categories/');

const useCalendarCategories = (isMainPage = true) => {
  const { t } = useTranslation();
  let { id } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const [form] = Form.useForm();
  const {
    data,
    onChangeTableFilter,
    loading,
    updateInProcess,
    singleItem,
    setUpdateInProcess,
    onDelete,
    setLoading,
  } = useApi({
    resourceKey: 'calendar_categories',
    singleResourceKey: 'calendar_category',
    api: api,
    isMainPage,
    adapter: EmergencyContactsAdapter,
  });

  const columns = useMemo(() => {
    return [
      {
        title: t('Titolo'),
        dataIndex: 'title',
        sorter: () => {},
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 180,
        fixed: 'right',
        render: (data, row) => (row.can_update !== false || row.can_destroy !== false) && (
          <Row
            gutter={16}
            justify={'center'}
            onClick={(e) => e.stopPropagation()}
          >
            {row.can_update !== false && <Col>
              <Link to={`${pathname}/${row.id}/edit`}>
                <Button type={'secondary'} icon={<EditIcon />} />
              </Link>
            </Col>}
            {row.can_destroy !== false && <Col>
              <Link to={`${pathname}/${row.id}/delete`}>
                <Button type={'red'} icon={<DeleteIcon />} />
              </Link>
            </Col>}
          </Row>
        ),
      },
    ];
  }, [pathname]);

  const handleImportExcel = async (values) => {
    setUpdateInProcess(true);
    try {
      const formData = new FormData();
      for (let key in values) {
        if (key !== 'file') {
          formData.append(`${key}`, values[key] || '');
        } else {
          if (values[key]) {
            formData.append(`${key}`, values[key][0]?.originFileObj || '');
          }
        }
      }
      await api.importExcel(formData);
      navigate(UIHelper.getListRoute(pathname, 1));
    } catch (e) {
      const errors = e?.response?.data || [];
      if (errors.length) {
        form.setFields([
          {
            name: 'shopping_center_id',
            errors,
          },
        ]);
      } else {
        form.setFields([
          {
            name: 'file',
            errors: [t('Wrong File')],
          },
        ]);
      }
    } finally {
      setUpdateInProcess(false);
    }
  };

  useEffect(() => {
    if (!isMainPage && singleItem) {
      form.setFields([
        {
          name: 'title',
          value: singleItem.title,
        },
      ]);
    }
  }, [singleItem, isMainPage]);

  const handleCreateOrUpdate = async (values) => {
    setLoading(true);
    try {
      const data = {
        ...values,
      };
      await api.put(id, { data });
      navigate(UIHelper.getListRoute(pathname));
    } catch (e) {
      const titleErrors = e?.response?.data?.title || [];
      const negoIdErrors = e?.response?.data?.nego_id || [];
      form.setFields([
        {
          name: 'title',
          errors: titleErrors,
        },
        {
          name: 'nego_id',
          errors: negoIdErrors,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return {
    columns,
    data,
    onChangeTableFilter,
    loading,
    onDelete,
    updateInProcess,
    shoppingCenters,
    form,
    handleImportExcel,
    handleCreateOrUpdate,
  };
};

export default useCalendarCategories;
